/**
 * InitialDataSlice - Fetches all initial app data in a single API call
 * This improves performance by reducing multiple sequential API requests
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { logError } from "../helpers/utils";

// Single API call to fetch all initial data
export const getInitialData = createAsyncThunk(
  "InitialData/getInitialData",
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if data already exists to avoid refetching
      const state = getState();
      if (state.initialData.loaded) {
        return null; // Skip fetch if already loaded
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/homepage-data`
      );
      return response.data.data;
    } catch (error) {
      logError(error, "InitialData/getInitialData");
      return rejectWithValue(error.response?.data || 'Failed to load initial data');
    }
  }
);

export const InitialDataSlice = createSlice({
  name: "initialData",
  initialState: {
    loaded: false,
    loading: false,
    error: null,
    // Data will be distributed to other slices via extraReducers
  },
  reducers: {
    resetInitialData: (state) => {
      state.loaded = false;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInitialData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInitialData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload !== null) {
          state.loaded = true;
        }
      })
      .addCase(getInitialData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error("Failed to load app data");
      });
  },
});

export const { resetInitialData } = InitialDataSlice.actions;
export default InitialDataSlice.reducer;
